import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { PosthogService } from 'src/shared/services/posthog.service';

import { UpdateRequiredComponent } from 'src/shared/components/update-required/update-required.component';

import { isVersionBelow } from 'src/shared/util/app-version';

import { FEATURE_FLAGS } from 'src/shared/constants/feature-flags';

import { environment } from 'src/environments/environment';

type NativePlatform = 'ios' | 'android';

export const STORE_URLS: Record<NativePlatform, string> = {
  ios: 'https://apps.apple.com/app/id1133337954',
  android: 'https://play.google.com/store/apps/details?id=com.tidy.clientmobile',
};

// Resuming the app re-reads the flag from PostHog's servers at most this often.
export const RESUME_RECHECK_INTERVAL_MS = 60 * 60 * 1000;

export interface RequiredUpdate {
  platform: NativePlatform;
  currentVersion: string;
  minimumVersion: string;
  storeUrl: string;
}

/**
 * Asks customers on a native build older than the minimum supported version to update.
 *
 * The minimum comes from the payload of a PostHog flag: a JSON string such as `"1.92.0"`,
 * applied to iOS and Android alike since both ship the package.json version. It fails open:
 * no flag, no payload, a payload that isn't a version, the web build or any error means no
 * prompt.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {

  private posthogService = inject(PosthogService);
  private modalController = inject(ModalController);

  private isPromptOpen = false;
  private lastCheckAt = 0;
  private resumeListenerAdded = false;

  async start(): Promise<void> {
    if (!this.getNativePlatform()) {
      return;
    }
    if (!this.resumeListenerAdded) {
      this.resumeListenerAdded = true;
      // A customer can keep the app in the background for weeks without a cold launch.
      App.addListener('resume', () => this.onResume());
    }
    await this.promptIfUpdateRequired();
  }

  async getRequiredUpdate(): Promise<RequiredUpdate | null> {
    const platform = this.getNativePlatform();
    if (!platform) {
      return null;
    }
    try {
      const payload = await this.posthogService.getFeatureFlagPayload(this.getFlagKey());
      const minimumVersion = this.readMinimumVersion(payload);
      if (!minimumVersion) {
        return null;
      }
      const { version } = await App.getInfo();
      if (!isVersionBelow(version, minimumVersion)) {
        return null;
      }
      return { platform, currentVersion: version, minimumVersion, storeUrl: STORE_URLS[platform] };
    } catch (error) {
      console.error('AppUpdate: Error checking the minimum version:', error);
      return null;
    }
  }

  async promptIfUpdateRequired(): Promise<void> {
    if (this.isPromptOpen) {
      return;
    }
    this.lastCheckAt = Date.now();
    const update = await this.getRequiredUpdate();
    if (!update || this.isPromptOpen) {
      return;
    }
    this.isPromptOpen = true;
    try {
      this.posthogService.capture('app_update_required_shown', {
        current_version: update.currentVersion,
        minimum_version: update.minimumVersion,
      });
      const modal = await this.modalController.create({
        component: UpdateRequiredComponent,
        componentProps: { storeUrl: update.storeUrl },
        backdropDismiss: false,
        canDismiss: false,
        cssClass: 'update-required-modal',
      });
      await modal.present();
    } catch (error) {
      this.isPromptOpen = false;
      console.error('AppUpdate: Error showing the update screen:', error);
    }
  }

  private async onResume(): Promise<void> {
    if (this.isPromptOpen || Date.now() - this.lastCheckAt < RESUME_RECHECK_INTERVAL_MS) {
      return;
    }
    this.lastCheckAt = Date.now();
    await this.posthogService.waitForIdentification();
    await this.posthogService.reloadFeatureFlags();
    await this.promptIfUpdateRequired();
  }

  private getNativePlatform(): NativePlatform | null {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android' ? platform : null;
  }

  private getFlagKey(): string {
    return environment.environment === 'production'
      ? FEATURE_FLAGS.CLIENT_APP_MINIMUM_VERSION
      : FEATURE_FLAGS.CLIENT_APP_MINIMUM_VERSION_SANDBOX;
  }

  // Not an object payload: the Android SDK parses one into a Java Map, which the Capacitor
  // plugin passes to JS as the text "{ios=1.92.0}". Some SDKs return the raw JSON text
  // ("\"1.92.0\"") instead of the parsed string.
  private readMinimumVersion(payload: unknown): string | null {
    if (typeof payload !== 'string') {
      return null;
    }
    try {
      const parsed = JSON.parse(payload);
      return typeof parsed === 'string' ? parsed : payload;
    } catch {
      return payload;
    }
  }
}
